import {
  Scene,
  Vector3,
  MeshBuilder,
  StandardMaterial,
  Color3,
  Mesh,
  HemisphericLight,
  DirectionalLight,
} from '@babylonjs/core';
import { ObbyLevel, ObbyLevelData, Platform } from './ObbyLevel';
import { Player } from '../../player/Player';
import { ObbyHUD } from './ObbyHUD';

interface PlatformMesh {
  mesh: Mesh;
  data: Platform;
  initialPosition: Vector3;
  time: number;
}

export class ObbyGame {
  private scene: Scene;
  private player: Player | null = null;
  private hud: ObbyHUD;
  private levelData: ObbyLevelData;
  private platforms: PlatformMesh[] = [];
  private lights: (HemisphericLight | DirectionalLight)[] = [];

  private currentCheckpoint: Vector3;
  private startTime: number = 0;
  private elapsedTime: number = 0;
  private isRunning: boolean = false;
  private isFinished: boolean = false;
  private deaths: number = 0;

  private onFinishCallback: ((time: number, deaths: number) => void) | null = null;
  private onExitCallback: (() => void) | null = null;

  constructor(scene: Scene) {
    this.scene = scene;
    this.levelData = ObbyLevel.createLevel1();
    this.currentCheckpoint = this.levelData.spawnPoint.clone();
    this.hud = new ObbyHUD();

    this.hud.onExit(() => {
      if (this.onExitCallback) {
        this.onExitCallback();
      }
    });

    this.hud.onRestart(() => {
      this.restart();
    });
  }

  public initialize(): void {
    this.createLighting();
    this.createPlatforms();
    this.hud.show();
  }

  private createLighting(): void {
    // Clear existing lights
    this.scene.lights.forEach(light => light.dispose());

    // Ambient light
    const hemi = new HemisphericLight('obbyHemi', new Vector3(0, 1, 0), this.scene);
    hemi.intensity = 0.6;
    hemi.diffuse = new Color3(1, 1, 1);
    hemi.groundColor = new Color3(0.3, 0.3, 0.4);
    this.lights.push(hemi);

    // Directional light for shadows
    const dir = new DirectionalLight('obbyDir', new Vector3(-1, -2, -1), this.scene);
    dir.intensity = 0.8;
    dir.diffuse = new Color3(1, 0.95, 0.9);
    this.lights.push(dir);

    // Set sky color
    this.scene.clearColor = this.levelData.skyColor.toColor4(1);
  }

  private createPlatforms(): void {
    for (const platformData of this.levelData.platforms) {
      const mesh = MeshBuilder.CreateBox(
        `platform_${platformData.type}_${this.platforms.length}`,
        {
          width: platformData.size.x,
          height: platformData.size.y,
          depth: platformData.size.z,
        },
        this.scene
      );

      mesh.position = platformData.position.add(
        new Vector3(0, platformData.size.y / 2, 0)
      );

      const material = new StandardMaterial(`mat_${mesh.name}`, this.scene);
      material.diffuseColor = platformData.color.clone();

      // Special effects for different platform types
      if (platformData.type === 'checkpoint') {
        material.emissiveColor = platformData.color.scale(0.3);
      } else if (platformData.type === 'kill') {
        material.emissiveColor = new Color3(0.5, 0, 0);
      } else if (platformData.type === 'finish') {
        material.emissiveColor = platformData.color.scale(0.4);
      } else if (platformData.type === 'bouncy') {
        material.emissiveColor = new Color3(0.3, 0.1, 0.2);
      }

      material.specularColor = new Color3(0.2, 0.2, 0.2);
      mesh.material = material;
      mesh.checkCollisions = true;
      mesh.receiveShadows = true;

      this.platforms.push({
        mesh,
        data: platformData,
        initialPosition: platformData.position.clone(),
        time: Math.random() * Math.PI * 2, // Random phase for moving platforms
      });
    }
  }

  public setPlayer(player: Player): void {
    this.player = player;
    this.player.setPosition(this.currentCheckpoint);
  }

  public start(): void {
    this.isRunning = true;
    this.isFinished = false;
    this.startTime = Date.now();
    this.deaths = 0;
    this.hud.setDeaths(this.deaths);
  }

  public restart(): void {
    this.currentCheckpoint = this.levelData.spawnPoint.clone();
    this.deaths = 0;
    this.elapsedTime = 0;
    this.isFinished = false;
    this.isRunning = true;
    this.startTime = Date.now();
    this.hud.setDeaths(this.deaths);
    this.hud.hideFinish();

    if (this.player) {
      this.player.setPosition(this.currentCheckpoint);
    }
  }

  public update(deltaTime: number): void {
    if (!this.player || !this.isRunning) return;

    // Update timer
    if (!this.isFinished) {
      this.elapsedTime = (Date.now() - this.startTime) / 1000;
      this.hud.setTime(this.elapsedTime);
    }

    // Update moving platforms
    this.updateMovingPlatforms(deltaTime);

    // Check platform collisions
    this.checkPlatformCollisions();
  }

  private updateMovingPlatforms(deltaTime: number): void {
    for (const platform of this.platforms) {
      if (platform.data.type === 'moving' && platform.data.movement) {
        platform.time += deltaTime * platform.data.movement.speed;

        const offset = Math.sin(platform.time) * platform.data.movement.distance;
        const newPos = platform.initialPosition.clone();

        switch (platform.data.movement.axis) {
          case 'x':
            newPos.x += offset;
            break;
          case 'y':
            newPos.y += offset;
            break;
          case 'z':
            newPos.z += offset;
            break;
        }

        platform.mesh.position = newPos.add(
          new Vector3(0, platform.data.size.y / 2, 0)
        );
      }
    }
  }

  private checkPlatformCollisions(): void {
    if (!this.player) return;

    const playerPos = this.player.getPosition();
    const playerFeet = playerPos.y;
    const physics = this.player.getPhysics();

    for (const platform of this.platforms) {
      const collision = this.checkPlatformCollision(playerPos, physics.velocity.y, platform);

      if (collision.onTop) {

        // Snap player to platform top
        physics.setGroundedAt(collision.groundY);

        // Handle special platform types
        switch (platform.data.type) {
          case 'checkpoint':
          case 'start':
            this.setCheckpoint(platform.data.position);
            break;
          case 'kill':
            this.die();
            return; // Stop checking after death
          case 'finish':
            this.finish();
            return;
          case 'bouncy':
            this.bounce();
            return;
        }
      }
    }

    // Check if player fell off the map
    if (playerFeet < -10) {
      this.die();
    }
  }

  private checkPlatformCollision(
    playerPos: Vector3,
    velocityY: number,
    platform: PlatformMesh
  ): { onTop: boolean; groundY: number } {
    const platPos = platform.mesh.position;
    const size = platform.data.size;

    const halfWidth = size.x / 2 + 0.3; // Small margin for easier landing
    const halfDepth = size.z / 2 + 0.3;
    const topY = platPos.y + size.y / 2;

    // Check if player is within platform bounds horizontally
    const inX = playerPos.x >= platPos.x - halfWidth && playerPos.x <= platPos.x + halfWidth;
    const inZ = playerPos.z >= platPos.z - halfDepth && playerPos.z <= platPos.z + halfDepth;

    if (!inX || !inZ) {
      return { onTop: false, groundY: 0 };
    }

    // Check if player is landing on top of the platform (falling down)
    // Player feet should be at or slightly above platform top
    const isAbovePlatform = playerPos.y >= topY - 0.1;
    const isNearPlatform = playerPos.y <= topY + 1.5;
    const isFalling = velocityY <= 0;

    if (isAbovePlatform && isNearPlatform && isFalling) {
      return { onTop: true, groundY: topY };
    }

    return { onTop: false, groundY: 0 };
  }

  private setCheckpoint(position: Vector3): void {
    const newCheckpoint = position.add(new Vector3(0, 2, 0));

    // Only update if it's a new checkpoint
    if (!this.currentCheckpoint.equals(newCheckpoint)) {
      this.currentCheckpoint = newCheckpoint;
      this.hud.showCheckpoint();
    }
  }

  private die(): void {
    this.deaths++;
    this.hud.setDeaths(this.deaths);

    if (this.player) {
      this.player.setPosition(this.currentCheckpoint);
    }
  }

  private bounce(): void {
    if (this.player) {
      const physics = this.player.getPhysics();
      physics.velocity.y = 20; // Strong upward bounce
    }
  }

  private finish(): void {
    if (this.isFinished) return;

    this.isFinished = true;
    this.isRunning = false;
    this.hud.showFinish(this.elapsedTime, this.deaths);

    if (this.onFinishCallback) {
      this.onFinishCallback(this.elapsedTime, this.deaths);
    }
  }

  public onFinish(callback: (time: number, deaths: number) => void): void {
    this.onFinishCallback = callback;
  }

  public onExit(callback: () => void): void {
    this.onExitCallback = callback;
  }

  public getSpawnPoint(): Vector3 {
    return this.levelData.spawnPoint.clone();
  }

  public setPointerLocked(locked: boolean): void {
    this.hud.setPointerLocked(locked);
  }

  public dispose(): void {
    // Dispose platforms
    for (const platform of this.platforms) {
      if (platform.mesh.material) {
        platform.mesh.material.dispose();
      }
      platform.mesh.dispose();
    }
    this.platforms = [];

    // Dispose lights
    for (const light of this.lights) {
      light.dispose();
    }
    this.lights = [];

    // Dispose HUD
    this.hud.dispose();
  }
}
